'use client';
import React, { useState } from 'react';
import Badge from '@/components/ui/Badge';
import Icon from '@/components/ui/AppIcon';

const routePlans = [
  {
    id: 'rp-0041',
    eventId: 'evt-8841',
    mode: 'mixed',
    eta: 18.4,
    riskScore: 0.31,
    assets: ['HMAS-Ballarat', 'AUV-09', 'MH-60R Seahawk'],
    waypoints: 4,
    status: 'En Route',
    notes: 'Favorable currents +2kt. Ice-free corridor confirmed.',
  },
  {
    id: 'rp-0040',
    eventId: 'evt-8840',
    mode: 'surface',
    eta: 34.2,
    riskScore: 0.48,
    assets: ['MV-Resolute', 'Coast Guard CG-7702'],
    waypoints: 6,
    status: 'Dispatched',
    notes: 'Sea state 3. Political zone avoidance route applied.',
  },
];

export default function RoutePlanPanel() {
  const [expanded, setExpanded] = useState<string | null>('rp-0041');

  return (
    <div className="bg-card border border-border rounded-xl flex flex-col" style={{ minHeight: 420 }}>
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Active Route Plans</h2>
            <p className="text-xs text-muted-foreground">Distress-linked rescue routing</p>
          </div>
          <Badge variant="negative" dot>{routePlans?.length} Active</Badge>
        </div>
      </div>
      <div className="flex-1 p-4 space-y-3 overflow-y-auto scrollbar-ocean">
        {routePlans?.map((plan) => (
          <div
            key={plan?.id}
            className={`rounded-xl border transition-all duration-200 overflow-hidden ${
              expanded === plan?.id ? 'border-primary/30 bg-primary/5' : 'border-border bg-muted/30'
            }`}
          >
            <button
              onClick={() => setExpanded(expanded === plan?.id ? null : plan?.id)}
              className="w-full px-4 py-3 flex items-center justify-between text-left"
            >
              <div className="flex items-center gap-3">
                <div className="p-1.5 rounded-lg bg-negative/10 border border-negative/20">
                  <Icon name="MapPinIcon" size={14} className="text-negative" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground font-mono">{plan?.id}</p>
                  <p className="text-[10px] text-muted-foreground">→ {plan?.eventId}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={plan?.status === 'En Route' ? 'positive' : 'warning'} dot>
                  {plan?.status}
                </Badge>
                <Icon
                  name={expanded === plan?.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={14}
                  className="text-muted-foreground"
                />
              </div>
            </button>

            {expanded === plan?.id && (
              <div className="px-4 pb-4 space-y-3 fade-in">
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">ETA</p>
                    <p className="text-lg font-bold text-foreground font-tabular">{plan?.eta}m</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Risk Score</p>
                    <p className={`text-lg font-bold font-tabular ${plan?.riskScore < 0.4 ? 'text-positive' : 'text-warning'}`}>
                      {plan?.riskScore?.toFixed(2)}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Assets Dispatched</p>
                  <div className="flex flex-wrap gap-1.5">
                    {plan?.assets?.map((a) => (
                      <span key={`asset-${plan?.id}-${a}`} className="px-2 py-0.5 bg-muted rounded-md text-[10px] text-foreground border border-border">
                        {a}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                  <Icon name="MapIcon" size={12} />
                  <span>{plan?.waypoints} waypoints · {plan?.mode} mode</span>
                </div>
                <p className="text-[11px] text-muted-foreground bg-muted/30 rounded-lg px-3 py-2 border border-border">
                  {plan?.notes}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="px-5 py-3 border-t border-border">
        <button className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-primary/10 border border-primary/20 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors duration-150">
          <Icon name="PlusIcon" size={14} />
          Compute New Route
        </button>
      </div>
    </div>
  );
}